import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import StopOutlined from "@mui/icons-material/StopOutlined";
import RecordVoiceOverOutlined from "@mui/icons-material/RecordVoiceOverOutlined";
import { GlobalContext } from "../../../context/GlobalContext";

const REGION_FILTERS = [
  { id: "All", label: "All regions" },
  { id: "Americas", label: "Americas" },
  { id: "Asia", label: "Asia-Pacific" },
  { id: "Europe", label: "Europe" },
  { id: "UK", label: "UK & Ireland" },
  { id: "Africa & Middle East", label: "Africa & Middle East" },
  { id: "Global", label: "Global / neutral" },
];

function VoiceRow({ voice, selected, playing, onSelect, onPlay, lang }) {
  return (
    <Box
      onClick={() => onSelect(voice)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: "2px solid",
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: selected ? (t) => alpha(t.palette.primary.main, 0.06) : "background.paper",
        cursor: "pointer",
        transition: "border-color 0.15s",
        "&:hover": { borderColor: selected ? "primary.main" : "primary.light" },
      }}
    >
      <IconButton
        size="small"
        color={playing ? "error" : "primary"}
        onClick={(e) => {
          e.stopPropagation();
          onPlay(voice);
        }}
        aria-label={playing ? lang?.videoStudioVoiceStop || "Stop" : lang?.videoStudioVoicePlay || "Play sample"}
      >
        {playing ? <StopOutlined fontSize="small" /> : <PlayArrowOutlined fontSize="small" />}
      </IconButton>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {voice.label}
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.25 }}>
          <Chip size="small" label={voice.gender === "female" ? "Female" : "Male"} variant="outlined" />
          {(voice.regions || []).slice(0, 2).map((r) => (
            <Chip key={r} size="small" label={r} variant="outlined" sx={{ opacity: 0.85 }} />
          ))}
        </Stack>
      </Box>
      {selected ? (
        <Chip size="small" color="primary" label={lang?.videoStudioVoiceSelected || "Selected"} />
      ) : null}
    </Box>
  );
}

/**
 * @param {{
 *   open: boolean;
 *   onClose: () => void;
 *   value?: string;
 *   onConfirm: (voice: { id: string; label: string }) => void;
 *   lang?: object;
 * }} props
 */
export default function KieVoicePickerModal({ open, onClose, value, onConfirm, lang }) {
  const { hitAxios } = React.useContext(GlobalContext);
  const [loading, setLoading] = React.useState(false);
  const [voices, setVoices] = React.useState([]);
  const [pick, setPick] = React.useState(null);
  const [genderFilter, setGenderFilter] = React.useState("all");
  const [regionFilter, setRegionFilter] = React.useState("All");
  const [search, setSearch] = React.useState("");
  const [playingId, setPlayingId] = React.useState(null);
  const audioRef = React.useRef(null);

  const loadVoices = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await hitAxios({
        path: "/api/video-studio/kie-voices",
        post: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (res?.data?.success && Array.isArray(res.data.data?.voices)) {
        setVoices(res.data.data.voices);
      } else {
        setVoices([]);
      }
    } finally {
      setLoading(false);
    }
  }, [hitAxios]);

  React.useEffect(() => {
    if (!open) return;
    loadVoices();
    setSearch("");
    setGenderFilter("all");
    setRegionFilter("All");
  }, [open, loadVoices]);

  React.useEffect(() => {
    if (!open) return;
    const found = voices.find((v) => v.id === value);
    setPick(found || null);
  }, [open, value, voices]);

  React.useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;
    const onEnd = () => setPlayingId(null);
    el.addEventListener("ended", onEnd);
    return () => el.removeEventListener("ended", onEnd);
  }, []);

  React.useEffect(() => {
    if (!open) {
      audioRef.current?.pause();
      setPlayingId(null);
    }
  }, [open]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return voices.filter((v) => {
      if (genderFilter !== "all" && v.gender !== genderFilter) return false;
      if (regionFilter !== "All" && !(v.regions || []).includes(regionFilter)) return false;
      if (q && !String(v.label || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [voices, genderFilter, regionFilter, search]);

  const playSample = (voice) => {
    if (!voice?.sampleUrl) return;
    if (playingId === voice.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    const el = audioRef.current;
    if (!el) return;
    el.src = voice.sampleUrl;
    el.play().catch(() => setPlayingId(null));
    setPlayingId(voice.id);
  };

  const regionLabel = (id) => {
    const row = REGION_FILTERS.find((r) => r.id === id);
    if (!row) return id;
    const key = `videoStudioVoiceRegion${id.replace(/[^a-zA-Z]/g, "")}`;
    return lang?.[key] || row.label;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
        <RecordVoiceOverOutlined color="primary" />
        <Box sx={{ flex: 1 }}>
          {lang?.videoStudioKieVoiceModalTitle || "Choose Kie AI voice"}
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseOutlined />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {lang?.videoStudioKieVoiceModalHint ||
              "Preview style samples from Kie AI, filter by gender or region, then select a voice for Kling voiceover."}
          </Typography>

          <TextField
            size="small"
            fullWidth
            label={lang?.search || "Search"}
            placeholder={lang?.videoStudioKieVoiceSearchPh || "Search by name…"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <ToggleButtonGroup
            exclusive
            size="small"
            value={genderFilter}
            onChange={(_, v) => v != null && setGenderFilter(v)}
            sx={{ flexWrap: "wrap" }}
          >
            <ToggleButton value="all">{lang?.ugcVoiceGenderAll || "All"}</ToggleButton>
            <ToggleButton value="female">{lang?.ugcVoiceGenderFemale || "Female"}</ToggleButton>
            <ToggleButton value="male">{lang?.ugcVoiceGenderMale || "Male"}</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            select
            size="small"
            label={lang?.ugcVoiceRegionLabel || "Region / market"}
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            fullWidth
          >
            {REGION_FILTERS.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {regionLabel(r.id)}
              </MenuItem>
            ))}
          </TextField>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : null}

          {!loading && filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
              {lang?.videoStudioKieVoiceNoMatch ||
                "No voices match these filters — try All regions or another gender."}
            </Typography>
          ) : null}

          <Stack spacing={1} sx={{ maxHeight: 360, overflow: "auto" }}>
            {filtered.map((v) => (
              <VoiceRow
                key={v.id}
                voice={v}
                selected={pick?.id === v.id}
                playing={playingId === v.id}
                onSelect={setPick}
                onPlay={playSample}
                lang={lang}
              />
            ))}
          </Stack>
        </Stack>
        <audio ref={audioRef} style={{ display: "none" }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{lang?.cancel || "Cancel"}</Button>
        <Button
          variant="contained"
          disabled={!pick}
          onClick={() => {
            if (pick) onConfirm(pick);
            onClose();
          }}
        >
          {lang?.videoStudioVoiceUseSelected || "Use voice"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
